import React, { useContext, useEffect, useRef, useState } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import OnboardingContainer from 'components/smart/onboarding-container/OnboardingContainer'

import Rockets from '../../assets/rockets.svg'

import AdxInput from 'components/custom/AdxInput'
import AdxPhoneNumber from 'components/reusable/AdxPhoneNumber/AdxPhoneNumber'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'

import { countries } from 'services/api/dropdown'
import { superAdminOnboardingLink } from 'module/sign-up/config/superAdminUrl'
import { SuperAdminOnboardingContext } from 'module/sign-up/context/SuperAdminOnboardingContext'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import axios from 'axios'
import { presignURL } from 'services/api/presign-url'
import { authContext } from 'contexts/AuthContext'
import { adminSignUp } from 'services/api/signup'
import Bugsnag from '@bugsnag/js'
import AdxButton from 'components/custom/AdxButton'

const StepFour = () => {
  const { auth } = useContext(authContext)
  const { savedSuperAdminOnboardingSession } = useContext(SuperAdminOnboardingContext)
  const navigate = useNavigate()

  const handleBack = () => {
    navigate(superAdminOnboardingLink(3))
  }

  // form states
  const [selectValueState, setSelectValueState] = useState('')
  const [selectValueCountry, setSelectValueCountry] = useState('')
  const [arrayOfStates, setArrayOfStates] = useState([])
  const formikRef = useRef()
  const { data: countriesQuery } = useQuery('countriesQuery', countries)

  const handleSetSelectState = (event) => {
    setSelectValueState(event.target.value)
  }

  const handleSetSelectCountry = (event) => {
    setSelectValueCountry(event?.target?.value)
  }

  // initial values
  const initialValues = {
    phone_no: '',
    phone_country_id: '132',
    address: '',
    postcode: '',
    state: '',
    country: '',
  }

  // form validation schema
  const validationSchema = Yup.object().shape({
    phone_country_id: Yup.string().required('Country code is required'),
    phone_no: Yup.string()
      .required('Company phone required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field '),
    address: Yup.string().required('Address is required'),
    postcode: Yup.string()
      .required('Postcode is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field '),
    state: Yup.string().required('State is required').nullable(),
    country: Yup.string().required('Country is required').nullable(),
  })

  // submission form function
  const queryClient = useQueryClient()
  const { isLoading: submitLoading, mutate: submitQuery } = useMutation(
    (values) => adminSignUp(values),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('myAccountData')
        navigate(superAdminOnboardingLink(5))
      },
      onError: async (err) => {
        const msg = Object.values(err?.data?.errors || [])[0] || 'An error occured'
        setErrorMessage(msg)
        handleErrorOpen()
      },
    }
  )

  // upload to s3
  const { isLoading: uploadLoading, mutate: uploadQuery } = useMutation(
    (values) => uploadPhoto(values?.profile_photo, values),
    {
      onSuccess: async (res) => {
        submitQuery(res)
      },
      onError: async (err) => {
        setErrorMessage(err.data.message)
        handleErrorOpen()
        Bugsnag.notify(err)
      },
    }
  )
  const uploadPhoto = async (photo, formValues) => {
    if (auth.data.tenant_id === undefined) {
      throw new Error('Internal error. Tenant id is undefined.')
    }

    const results = await presignURL({
      bucket: process.env.REACT_APP_AWS_BUCKET,
      key: `tenant_${auth.data.tenant_id}/profile_photo/` + photo.name,
    })
    const awsRequest = axios.create()
    delete awsRequest.defaults.headers.common['Authorization']
    await awsRequest.put(results.data.url, photo, {
      headers: {
        'Content-Type': photo.type,
      },
    })
    return formValues
  }

  const handleSubmit = (formikValues) => {
    const values = {
      profile_image: savedSuperAdminOnboardingSession.profile_photo.name,
      ...savedSuperAdminOnboardingSession,
      ...formikValues,
    }
    uploadQuery(values)
  }

  // error modal
  const [isErrorModalOpen, setErrorModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('An error occured')
  const handleErrorOpen = () => {
    setErrorModalOpen(true)
  }
  const handleErrorClose = () => {
    setErrorModalOpen(false)
  }

  // check session
  useEffect(() => {
    if (
      !savedSuperAdminOnboardingSession?.profile_photo &&
      !savedSuperAdminOnboardingSession?.name
    ) {
      navigate(superAdminOnboardingLink(2))
    } else if (
      savedSuperAdminOnboardingSession?.profile_photo &&
      savedSuperAdminOnboardingSession?.name &&
      !savedSuperAdminOnboardingSession?.designation
    ) {
      navigate(superAdminOnboardingLink(3))
    }
  }, [])

  return (
    <>
      <OnboardingContainer bgColorRight="white.main">
        <Box
          height={'100%'}
          display="flex"
          justifyContent={{ xs: 'center', lg: 'flex-end' }}
          alignItems="center"
        >
          <Box
            component="img"
            src={Rockets}
            position="fixed"
            bottom={0}
            right={0}
            width="calc(100vw * 0.8)"
            display={{ xs: 'none', lg: 'block' }}
          />
          <Box
            width={{ xs: '100%', lg: '500px' }}
            height={{ xs: '75vh', lg: '80vh' }}
            maxHeight={{ xs: 'unset', lg: '650px' }}
            mr={{ lg: 7.6875 }}
            p={{ xs: 2, lg: 0 }}
            position={'relative'}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box mb={4} display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="body1" color="alternate.main" fontWeight="bold">
                Step 3
              </Typography>
              <Typography variant="h4" textAlign="justify" color="dark-text.main" fontWeight="bold">
                Contact Details
              </Typography>
              <Box mt={4}>
                <Formik
                  enableReinitialize
                  validationSchema={validationSchema}
                  initialValues={initialValues}
                  onSubmit={(values) => handleSubmit(values)}
                  innerRef={formikRef}
                >
                  {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                    <Form>
                      <Box px="2px">
                        <Grid container spacing={2}>
                          <Grid item xs={12} lg={12}>
                            <AdxPhoneNumber
                              label="Phone Number"
                              placeholder="E.g: 123456789"
                              sxInput={{ width: '100%' }}
                              dropdownValue={values?.phone_country_id}
                              dropdownName="phone_country_id"
                              onDropdownChange={(value) => setFieldValue(`phone_country_id`, value)}
                              dropdownErrorMessage={
                                touched.phone_country_id && errors.phone_country_id
                              }
                              inputValue={values?.phone_no}
                              inputName="phone_no"
                              onInputChange={handleChange}
                              inputErrorMessage={touched.phone_no && errors.phone_no}
                              dropdownIsErrorShown={
                                touched.phone_country_id && errors.phone_country_id
                              }
                              inputIsErrorShown={touched.phone_no && errors.phone_no}
                            />
                          </Grid>
                          <Grid item xs={12} lg={12}>
                            <Field
                              as={AdxInput}
                              name="address"
                              label="Home address"
                              size="standard"
                              placeholder="E.g : H01-08-12, Pangsapuri Harmoni, Jalan Eco Majestic 3A/5, Semenyih"
                              multiline
                              minRows={3}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              errorMessage={
                                <ErrorMessage name="address" component={AdxErrorMessage} />
                              }
                              isErrorShown={errors.address && touched.address}
                              sxInput={{ py: 2 }}
                            />
                          </Grid>
                          <Grid item xs={12} lg={12}>
                            <Field
                              as={AdxDropdown}
                              name="country"
                              label="Country"
                              value={selectValueCountry}
                              onChange={(e) => {
                                handleSetSelectCountry(e)
                                setFieldValue('country', e.target.value)
                                setFieldValue('state', null)
                                setArrayOfStates(
                                  countriesQuery?.data.find((item) => item.name === e.target.value)
                                    .states
                                )
                              }}
                              isErrorShown={errors?.country && touched?.country}
                              errorMessage={
                                <ErrorMessage name="country" component={AdxErrorMessage} />
                              }
                            >
                              {countriesQuery?.data.map((item) => (
                                <AdxMenuItem value={item.name} selectedvalue={selectValueCountry}>
                                  {item.name}
                                </AdxMenuItem>
                              ))}
                            </Field>
                          </Grid>
                          <Grid item xs={12} lg={4}>
                            <Field
                              as={AdxInput}
                              name="postcode"
                              label="Postcode"
                              size="standard"
                              placeholder="Postcode"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              errorMessage={
                                <ErrorMessage name="postcode" component={AdxErrorMessage} />
                              }
                              isErrorShown={errors.postcode && touched.postcode}
                            />
                          </Grid>
                          <Grid item xs={12} lg={8}>
                            <Field
                              as={AdxDropdown}
                              name="state"
                              label="State"
                              value={selectValueState}
                              onChange={(e) => {
                                setFieldValue('state', e.target.value)
                                handleSetSelectState(e)
                              }}
                              isErrorShown={errors.state && touched.state}
                              errorMessage={
                                <ErrorMessage name="state" component={AdxErrorMessage} />
                              }
                            >
                              {arrayOfStates?.map((item) => (
                                <AdxMenuItem value={item.name} selectedvalue={selectValueState}>
                                  {item.name}
                                </AdxMenuItem>
                              ))}
                            </Field>
                          </Grid>
                        </Grid>
                      </Box>
                      <button type="submit" hidden>
                        Submit
                      </button>
                    </Form>
                  )}
                </Formik>
              </Box>
              <Box width={1} mt={10} display="flex" gap={2}>
                <AdxButton
                  variant={'outlined'}
                  onClick={handleBack}
                  sx={{ minWidth: '20px', pl: 3, borderWidth: 2 }}
                >
                  {<ArrowBackIosIcon />}
                </AdxButton>
                <AdxButton
                  variant={'contained'}
                  fullWidth
                  onClick={formikRef?.current?.handleSubmit}
                  disabled={uploadLoading || submitLoading}
                  isLoading={uploadLoading || submitLoading}
                >
                  Submit
                </AdxButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </OnboardingContainer>

      {/* error modal */}
      <AdxInfoModal
        open={isErrorModalOpen}
        onClose={handleErrorClose}
        handleClose={handleErrorClose}
        muiIcon="error"
        iconColor="error.main"
        title="Error"
        subtitle={errorMessage || 'An error has occured.'}
        footerButtons
        firstBtnLabel={'Ok'}
        firstBtnHandle={handleErrorClose}
      ></AdxInfoModal>
    </>
  )
}

export default StepFour
